// Singularity.tsx — A point of singularity (event horizon) at a focused system's
// rim: the local mouth of a hyperlane, aimed at the system it leads to. A dark core
// (the horizon) ringed by a glowing accretion torus that faces the destination like
// a portal, plus a soft halo. Floored to a visible apparent size like a planet, it
// hovers (bracket + label) and selects (click) — selecting opens its detail sheet
// with the destination and transit time. Does not orbit; it sits on the rim.
import { useMemo, useRef, useState } from 'react';
import { EntityLabel } from './EntityLabel';
import { useFrame, useThree } from '@react-three/fiber';
import { AdditiveBlending, DoubleSide, Quaternion, Vector3 } from 'three';
import type { Group } from 'three';
import type { ThreeEvent } from '@react-three/fiber';
import { useGame } from '../state/context';
import { apparentScale, FLOOR_MOON } from './apparentScale';
import { glowTexture } from './glow';
import { Bracket } from './Bracket';
import type { Gate } from './hyperlane';

interface Props {
  gate: Gate;
  /** The focused system's galaxy position (this group's parent origin). */
  systemPos: [number, number, number];
  /** Rim radius to seat the singularity at, scene units. */
  rim: number;
}

// Event-horizon core radius (true size, before the apparent-size floor). Small —
// a singularity is a pinprick, not a planet — with only a modest moon-sized floor
// so it stays selectable without bloating.
const CORE = 0.05;
const UNIT_Z = new Vector3(0, 0, 1);

export function Singularity({ gate, systemPos, rim }: Props) {
  const { hover, hoveredId, focus, selectedSingularityId } = useGame();
  const [local, setLocal] = useState(false);
  const camera = useThree((state) => state.camera);
  const bodyRef = useRef<Group>(null);
  const glow = useMemo(glowTexture, []);

  const dir = useMemo(() => new Vector3(...gate.dir).normalize(), [gate.dir]);
  const localPos = useMemo(() => dir.clone().multiplyScalar(rim), [dir, rim]);
  const worldPos = useMemo(
    () => new Vector3(...systemPos).add(localPos),
    [systemPos, localPos],
  );
  // Face the accretion ring (default axis +Z) toward the destination — a portal.
  const quaternion = useMemo(() => new Quaternion().setFromUnitVectors(UNIT_Z, dir), [dir]);

  const selected = selectedSingularityId === gate.id;
  const hovered = local || hoveredId === gate.id;
  const accent = selected ? '#ffd27a' : '#6fd0ff';
  const halo = selected ? '#ffc58f' : '#8fb6ff';

  useFrame(() => {
    if (bodyRef.current) {
      bodyRef.current.scale.setScalar(apparentScale(camera, worldPos, CORE, FLOOR_MOON));
    }
  });

  const onOver = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    setLocal(true);
    hover(gate.id);
    document.body.style.cursor = 'pointer';
  };
  const onOut = () => {
    setLocal(false);
    hover(null);
    document.body.style.cursor = 'auto';
  };
  const onClick = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    focus(gate.id);
  };

  return (
    <group position={localPos}>
      <group ref={bodyRef}>
        <group quaternion={quaternion}>
          {/* Thin bright accretion/photon ring — faces the destination. */}
          <mesh>
            <torusGeometry args={[CORE * 2.4, CORE * 0.11, 12, 80]} />
            <meshBasicMaterial
              color={accent}
              transparent
              opacity={0.95}
              depthWrite={false}
              blending={AdditiveBlending}
              toneMapped={false}
              side={DoubleSide}
            />
          </mesh>
        </group>
        {/* Event horizon — the dark core, and the pointer hit target. */}
        <mesh onClick={onClick} onPointerOver={onOver} onPointerOut={onOut}>
          <sphereGeometry args={[CORE, 24, 18]} />
          <meshBasicMaterial color="#05040a" toneMapped={false} />
        </mesh>
        {/* Tight lensing glow so it reads as a faint point at distance. */}
        <sprite scale={[CORE * 4.5, CORE * 4.5, 1]} renderOrder={1}>
          <spriteMaterial
            map={glow}
            color={halo}
            transparent
            opacity={0.5}
            depthWrite={false}
            blending={AdditiveBlending}
            toneMapped={false}
          />
        </sprite>
      </group>
      {hovered || selected ? <Bracket /> : null}
      {/* Name shown by default; brightens when hovered or its sheet is open. */}
      <EntityLabel
        text={`↝ ${gate.neighbor.name}`}
        sub={`${gate.lightYears.toFixed(1)} ly`}
        lit={hovered || selected}
      />
    </group>
  );
}
